/**
 * <lm-drawer> — Lu Moments side sheet.
 *
 * Opened by any [data-drawer="<id>"] trigger anywhere on the page, by the URL hash, or by .open().
 * Closes on the ✕, the scrim, Escape, or a rightward swipe. Locks page scroll (Horizon's
 * `scroll-lock` attribute on <html>), traps focus inside the sheet, and returns focus to whatever
 * opened it.
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class LmDrawer extends HTMLElement {
  #opener = null;
  #touchX = null;

  connectedCallback() {
    this.sheet = this.querySelector('.lm-drawer__sheet');
    this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.addEventListener('keydown', this.#onKey);
    this.sheet?.addEventListener('touchstart', (e) => (this.#touchX = e.touches[0].clientX), { passive: true });
    this.sheet?.addEventListener('touchend', (e) => {
      if (this.#touchX == null) return;
      const dx = e.changedTouches[0].clientX - this.#touchX;
      this.#touchX = null;
      if (dx > 70) this.close();
    });
    if (location.hash === `#${this.id}`) requestAnimationFrame(() => this.open());
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open(opener) {
    if (this.isOpen) return;
    this.#opener = opener || document.activeElement;
    document.querySelectorAll('lm-drawer[open]').forEach((d) => d !== this && d.close());
    this.setAttribute('open', '');
    document.documentElement.setAttribute('scroll-lock', '');
    requestAnimationFrame(() => (this.sheet?.querySelector('.lm-drawer__close') || this.sheet)?.focus({ preventScroll: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    if (!document.querySelector('lm-drawer[open]')) document.documentElement.removeAttribute('scroll-lock');
    if (location.hash === `#${this.id}`) history.replaceState(null, '', location.pathname + location.search);
    const opener = this.#opener;
    this.#opener = null;
    if (opener && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
  }

  #onKey = (e) => {
    if (!this.isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key !== 'Tab' || !this.sheet) return;
    const items = [...this.sheet.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };
}

if (!customElements.get('lm-drawer')) customElements.define('lm-drawer', LmDrawer);

document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-drawer]');
  if (!trigger) return;
  const drawer = document.getElementById(trigger.dataset.drawer);
  if (drawer instanceof LmDrawer) {
    e.preventDefault();
    drawer.open(trigger);
  }
});

window.addEventListener('hashchange', () => {
  const drawer = location.hash ? document.getElementById(location.hash.slice(1)) : null;
  if (drawer instanceof LmDrawer) drawer.open();
});
